import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet } from 'lucide-react';

const SALARY_FAQS = [
  { q: 'Why is my in-hand salary much less than CTC?', a: 'CTC includes employer PF, gratuity, health insurance, and other benefits you never receive as cash. These can be 15–25% of CTC.' },
  { q: 'What is in-hand for ₹10 LPA?', a: 'Under the new tax regime, approximately ₹72,000–75,000/month, depending on city, employer structure, and components.' },
  { q: 'New vs old tax regime — which gives higher take-home?', a: 'New regime is better below ₹7L (zero tax with rebate). Old regime wins above ₹15L if you have heavy deductions — 80C, HRA, home loan.' },
  { q: 'Is PF deduction mandatory?', a: 'Yes if basic salary is ₹15,000/month or less. Above that, some employers allow opting out of EPF. Check your offer letter.' },
  { q: 'How do I increase my take-home without changing CTC?', a: 'Ask HR to restructure: increase special allowances (LTA, food coupons) and reduce basic. This lowers PF deduction while CTC stays the same.' },
  { q: 'Does HRA reduce take-home?', a: 'No — HRA is part of CTC. But if you live in rented accommodation, you can claim HRA exemption, reducing taxable income and increasing effective take-home.' },
];

export default function CalcSalary() {
  document.title = 'In-Hand Salary Calculator India — Budget Mantra';
  const [ctc, setCtc]       = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const annual = parseFloat(ctc);
    if (!annual) return;
    const basic   = annual * 0.4;
    const pf      = Math.min(basic * 0.12, 21600);
    const taxable = annual - annual * 0.4 * 0.5 * 0.4 - pf - 50000;
    const tax     = taxable > 1500000 ? (taxable - 1500000) * 0.3 + 187500
                  : taxable > 1000000 ? (taxable - 1000000) * 0.2 + 87500
                  : taxable > 500000  ? (taxable - 500000)  * 0.1 + 12500
                  : taxable > 300000  ? (taxable - 300000)  * 0.05
                  : 0;
    const inhand  = (annual - pf * 2 - tax) / 12;
    setResult({ monthly: inhand, pf: pf / 12, tax: tax / 12 });
  };

  const fmt = (n) => Math.round(Math.max(0, n)).toLocaleString('en-IN');

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
          <Wallet size={20} className="text-orange-500" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-stone-900">In-Hand Salary Calculator</h1>
          <p className="text-sm text-stone-500">Estimate your monthly take-home from CTC (India)</p>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4 max-w-md">
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1.5">Annual CTC (₹)</label>
          <input type="number" value={ctc} onChange={e => setCtc(e.target.value)} placeholder="1200000"
            className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-orange-400" />
        </div>
        <button onClick={calculate}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 rounded-xl transition-colors">
          Calculate Take-Home
        </button>
        {result && (
          <div className="bg-orange-50 rounded-xl p-4 space-y-2">
            <div className="text-center mb-3">
              <p className="text-xs text-stone-500 mb-1">Monthly In-Hand</p>
              <p className="text-3xl font-extrabold text-orange-600">₹{fmt(result.monthly)}</p>
            </div>
            <div className="flex justify-between text-xs text-stone-500">
              <span>PF deduction / month</span><span className="font-semibold text-stone-700">₹{fmt(result.pf)}</span>
            </div>
            <div className="flex justify-between text-xs text-stone-500">
              <span>Estimated tax / month</span><span className="font-semibold text-stone-700">₹{fmt(result.tax)}</span>
            </div>
          </div>
        )}
        <p className="text-[11px] text-stone-400">Approximate estimate. Actual amount depends on tax regime, HRA city, and employer.</p>
      </div>
      <p className="mt-6 text-sm text-stone-500">
        Budget your salary →{' '}
        <Link to="/login" className="text-orange-500 font-semibold hover:underline">Sign up free</Link>
      </p>
      <div className="mt-10 max-w-2xl space-y-8 text-stone-700 dark:text-stone-300">
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">What is In-Hand Salary?</h2>
          <p className="text-sm leading-7">Your in-hand or take-home salary is what you actually receive after all deductions — Provident Fund, Professional Tax, TDS, and other contributions. In India, CTC is always higher than take-home because it includes employer contributions and benefits that are never paid as cash.</p>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">How is it calculated?</h2>
          <p className="text-sm leading-7">In-Hand = CTC – Employee PF – Income Tax – Professional Tax. PF = 12% of Basic (Basic ≈ 40% of CTC). Standard deduction = ₹50,000/year. Example: ₹12 LPA → PF ₹57,600/year → Tax ≈ ₹1.08L → Take-home ≈ ₹87,000/month.</p>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-3">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {SALARY_FAQS.map(faq => (
              <div key={faq.q} className="border-b border-stone-100 dark:border-stone-800 pb-4">
                <p className="text-sm font-bold text-stone-900 dark:text-stone-100 mb-1">{faq.q}</p>
                <p className="text-sm leading-6 text-stone-600 dark:text-stone-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="text-lg font-extrabold text-stone-900 dark:text-stone-100 mb-2">Related Calculators</h2>
          <div className="flex flex-wrap gap-3">
            <Link to="/calc/emi" className="text-sm font-semibold text-orange-500 hover:underline">EMI Calculator →</Link>
            <Link to="/calc/sip" className="text-sm font-semibold text-orange-500 hover:underline">SIP Calculator →</Link>
            <Link to="/calc/tax" className="text-sm font-semibold text-orange-500 hover:underline">Income Tax →</Link>
          </div>
        </section>
        <section className="bg-orange-50 dark:bg-orange-950/30 rounded-2xl p-5">
          <p className="text-xs font-bold text-orange-500 uppercase tracking-wide mb-1">Chanakya says</p>
          <p className="text-sm leading-6 text-stone-700 dark:text-stone-300 mb-4">
            {result
              ? `Take-home of ₹${fmt(result.monthly)}/month — want me to build a budget around this and show you how much you can safely invest?`
              : `Enter your CTC and I'll estimate your take-home, then help you budget it properly.`}
          </p>
          <Link to="/signup" className="inline-block bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold px-5 py-2 rounded-xl transition-colors">
            Ask Chanakya free →
          </Link>
        </section>
      </div>
    </div>
  );
}
